/* Theme ArtWeb v1.0.0 · © Studio Art Web · https://studioartweb.com.br · não editar sem falar com o Studio */
/*
 * Harmony Health | hh-pdp.js
 * Página de produto padrão. Web components pequenos, sem biblioteca:
 * - <hh-pdp-galeria>: carrossel com deslize nativo (scroll-snap), miniaturas, setas e
 *   contador. Sem rolagem automática e sem prender a rolagem da página. Dois cliques
 *   (ou dois toques) na foto, ou o botão da lupa, abrem a tela cheia (<dialog>) com
 *   deslize, setas e teclado.
 * - <hh-pdp-compra>: troca de variante e de quantidade (1, 3 ou 6 frascos, Clube),
 *   recalcula os preços com os dados da Shopify, adiciona à sacola pelo /cart/add.js
 *   e avisa a sacola lateral do Horizon (CartAddEvent). Mostra a barra fixa do
 *   celular quando o botão de compra sai da tela por cima.
 * - <hh-pdp-recomendacoes>: busca as recomendações da Shopify para o produto.
 * Sem JavaScript, o formulário continua funcionando (envio normal para /cart/add).
 *
 * Parcelamento e duração (detalhes em docs/mecanismo-parcelamento.md):
 * - Os textos vêm do editor de temas (sections/hh-pdp-principal.liquid, grupo
 *   "Parcelamento e duração") e chegam aqui no JSON <script data-hh-dados>, em "textos".
 *   Campo vazio ou ausente cai no texto padrão de TEXTOS_PADRAO, igual ao default do schema.
 * - Número de parcelas e sem juros: "parcelas" e "semJuros" do mesmo JSON
 *   (settings.hh_parcelas e settings.hh_parcelas_sem_juros, de config/settings_schema.json).
 * - Marcadores: {total}, {parcelas}, {valor} (total / parcelas, arredondado ao centavo),
 *   {juros} (" " + texto sem juros, só com sem juros ligado). *trecho* sai em negrito.
 * - A primeira linha é feita no Liquid (snippets/hh-pdp-linha-cartao.liquid) com a mesma
 *   regra de linhaCartao() abaixo; ao trocar plano ou variante o JS refaz a linha.
 * - A duração de cada cartão (1 mês, {n} meses, {n} dias) é calculada só no Liquid e chega
 *   em data-duracao; o JS só a usa na barra fixa, pelo modelo "barraPlano".
 */
import { CartAddEvent } from '@theme/events';

const reduz = window.matchMedia('(prefers-reduced-motion: reduce)');

// Mesmos defaults do schema de sections/hh-pdp-principal.liquid
const TEXTOS_PADRAO = {
  cartaoParcelado: 'ou *{total}* em até {parcelas}x{juros}',
  cartaoAvista: 'ou *{total}* no cartão',
  parcela: 'ou *{parcelas}x* de {valor}{juros}',
  semJuros: 'sem juros',
  barraPlano: '{nome}, {duracao}',
};

const escapa = (t) =>
  String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** Troca os marcadores {chave} e transforma *trecho* em <b>trecho</b> (texto já escapado). */
function montaHtml(modelo, valores) {
  let t = escapa(modelo);
  for (const [k, v] of Object.entries(valores)) t = t.split(`{${k}}`).join(v);
  return t
    .split('*')
    .map((p, i) => (p === '' ? '' : i % 2 ? `<b>${p}</b>` : p))
    .join('');
}

class HHPdpGaleria extends HTMLElement {
  #ctrl = new AbortController();
  #atual = 0;
  #lupaAtual = 0;
  #toque = null;

  connectedCallback() {
    this.trilho = this.querySelector('[data-hh-trilho]');
    this.slides = [...this.querySelectorAll('[data-hh-slide]')];
    this.minis = [...this.querySelectorAll('[data-hh-mini]')];
    this.faixaMinis = this.querySelector('.hh-galeria__miniaturas');
    this.contador = this.querySelector('[data-hh-atual]');
    if (!this.trilho) return;
    const { signal } = this.#ctrl;
    this.#preparaLupa(signal);
    if (this.slides.length < 2) return;

    this.minis.forEach((b) => b.addEventListener('click', () => this.vai(Number(b.dataset.hhMini)), { signal }));
    this.querySelector('[data-hh-ant]')?.addEventListener('click', () => this.vai(this.#atual - 1), { signal });
    this.querySelector('[data-hh-prox]')?.addEventListener('click', () => this.vai(this.#atual + 1), { signal });
    this.trilho.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.vai(this.#atual + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.vai(this.#atual - 1); }
        if (e.key === 'Enter') { e.preventDefault(); this.abreLupa(this.#atual); }
      },
      { signal }
    );

    this.obs = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((en) => {
          if (en.isIntersecting && en.intersectionRatio >= 0.6) this.#marca(this.slides.indexOf(en.target));
        });
      },
      { root: this.trilho, threshold: [0.6] }
    );
    this.slides.forEach((s) => this.obs.observe(s));
  }

  disconnectedCallback() {
    this.#ctrl.abort();
    this.obs?.disconnect();
    this.obsLupa?.disconnect();
    if (this.lupa?.open) this.lupa.close();
  }

  vai(i) {
    const n = this.slides.length;
    const alvo = ((i % n) + n) % n;
    const slide = this.slides[alvo];
    const x = slide.offsetLeft - this.trilho.offsetLeft;
    this.trilho.scrollTo({ left: x, behavior: reduz.matches ? 'auto' : 'smooth' });
    this.#marca(alvo);
  }

  #marca(i) {
    if (i < 0) return;
    this.#atual = i;
    this.minis.forEach((b, k) => b.setAttribute('aria-current', String(k === i)));
    if (this.contador) this.contador.textContent = String(i + 1);
    this.#mostraMini(this.minis[i]);
  }

  /** Rola só a faixa de miniaturas (nunca a página) até a miniatura ativa ficar visível. */
  #mostraMini(b) {
    const f = this.faixaMinis;
    if (!b || !f) return;
    const cf = f.getBoundingClientRect();
    const cb = b.getBoundingClientRect();
    if (f.scrollWidth > f.clientWidth + 1) {
      if (cb.left < cf.left) f.scrollLeft -= cf.left - cb.left + 8;
      else if (cb.right > cf.right) f.scrollLeft += cb.right - cf.right + 8;
    }
    if (f.scrollHeight > f.clientHeight + 1) {
      if (cb.top < cf.top) f.scrollTop -= cf.top - cb.top + 8;
      else if (cb.bottom > cf.bottom) f.scrollTop += cb.bottom - cf.bottom + 8;
    }
  }

  /* ---------- tela cheia ---------- */

  #preparaLupa(signal) {
    this.lupa = this.querySelector('[data-hh-lupa]');
    // só os slides com foto entram na tela cheia (os cartões de número ficam de fora)
    this.fotos = this.slides.filter((s) => s.querySelector('img'));
    if (!this.lupa || !this.fotos.length || typeof this.lupa.showModal !== 'function') return;
    this.lupaTrilho = this.lupa.querySelector('[data-hh-lupa-trilho]');
    this.lupaConta = this.lupa.querySelector('[data-hh-lupa-atual]');
    const total = this.lupa.querySelector('[data-hh-lupa-total]');
    if (total) total.textContent = String(this.fotos.length);
    if (this.fotos.length < 2) this.lupa.classList.add('hh-lupa--uma');
    this.classList.add('hh-galeria--amplia');

    const amplia = this.querySelector('[data-hh-ampliar]');
    if (amplia) {
      amplia.hidden = false;
      amplia.addEventListener('click', () => this.abreLupa(this.#atual), { signal });
    }

    this.trilho.addEventListener(
      'dblclick',
      (e) => {
        const s = e.target.closest('[data-hh-slide]');
        if (s) { e.preventDefault(); this.abreLupa(this.slides.indexOf(s)); }
      },
      { signal }
    );
    // dois toques no celular: nem todo navegador gera dblclick no toque
    this.trilho.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType !== 'mouse') this.#toque = { x: e.clientX, y: e.clientY, t: e.timeStamp, ant: this.#toque?.ok ? this.#toque : null };
      },
      { signal }
    );
    this.trilho.addEventListener(
      'pointerup',
      (e) => {
        const tq = this.#toque;
        if (e.pointerType === 'mouse' || !tq) return;
        // arrastou: é deslize, não toque
        if (Math.hypot(e.clientX - tq.x, e.clientY - tq.y) > 12) { this.#toque = null; return; }
        tq.ok = true;
        const ant = tq.ant;
        tq.ant = null;
        if (ant && tq.t - ant.t < 320 && Math.hypot(tq.x - ant.x, tq.y - ant.y) < 40) {
          this.#toque = null;
          const s = e.target.closest('[data-hh-slide]');
          if (s) this.abreLupa(this.slides.indexOf(s));
        }
      },
      { signal }
    );
    this.trilho.addEventListener('pointercancel', () => { this.#toque = null; }, { signal });

    this.lupa.querySelector('[data-hh-lupa-fecha]')?.addEventListener('click', () => this.lupa.close(), { signal });
    this.lupa.querySelector('[data-hh-lupa-ant]')?.addEventListener('click', () => this.#lupaVai(this.#lupaAtual - 1), { signal });
    this.lupa.querySelector('[data-hh-lupa-prox]')?.addEventListener('click', () => this.#lupaVai(this.#lupaAtual + 1), { signal });
    this.lupa.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.#lupaVai(this.#lupaAtual + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.#lupaVai(this.#lupaAtual - 1); }
      },
      { signal }
    );
    // clique no fundo, fora da foto, também fecha
    this.lupa.addEventListener('click', (e) => { if (e.target.classList.contains('hh-lupa__foto')) this.lupa.close(); }, { signal });
    this.lupa.addEventListener(
      'close',
      () => {
        document.documentElement.style.removeProperty('overflow');
        // a galeria da página volta parada na última foto vista
        const slide = this.fotos[this.#lupaAtual];
        if (slide && this.slides.length > 1) {
          this.trilho.scrollTo({ left: slide.offsetLeft - this.trilho.offsetLeft, behavior: 'auto' });
          this.#marca(this.slides.indexOf(slide));
        }
      },
      { signal }
    );
  }

  #montaLupa() {
    if (this.lupaTrilho.childElementCount) return;
    this.fotos.forEach((s, i) => {
      const orig = s.querySelector('img');
      const fig = document.createElement('figure');
      fig.className = 'hh-lupa__foto';
      const img = document.createElement('img');
      if (orig.srcset) { img.srcset = orig.srcset; img.sizes = '(min-aspect-ratio: 1/1) 100vh, 100vw'; }
      img.src = orig.currentSrc || orig.src;
      img.alt = orig.alt || '';
      img.decoding = 'async';
      img.draggable = false;
      if (i) img.loading = 'lazy';
      fig.append(img);
      this.lupaTrilho.append(fig);
    });
    this.obsLupa = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((en) => {
          if (en.isIntersecting && en.intersectionRatio >= 0.6) this.#lupaMarca([...this.lupaTrilho.children].indexOf(en.target));
        });
      },
      { root: this.lupaTrilho, threshold: [0.6] }
    );
    [...this.lupaTrilho.children].forEach((f) => this.obsLupa.observe(f));
  }

  abreLupa(iSlide) {
    if (!this.lupaTrilho || this.lupa.open) return;
    this.#montaLupa();
    const iFoto = Math.max(0, this.fotos.indexOf(this.slides[iSlide]));
    document.documentElement.style.overflow = 'hidden';
    this.lupa.showModal();
    this.#lupaVai(iFoto, 'auto');
  }

  #lupaVai(i, comportamento) {
    const n = this.fotos.length;
    const alvo = ((i % n) + n) % n;
    const fig = this.lupaTrilho.children[alvo];
    if (!fig) return;
    this.lupaTrilho.scrollTo({ left: fig.offsetLeft - this.lupaTrilho.offsetLeft, behavior: comportamento || (reduz.matches ? 'auto' : 'smooth') });
    this.#lupaMarca(alvo);
  }

  #lupaMarca(i) {
    if (i < 0) return;
    this.#lupaAtual = i;
    if (this.lupaConta) this.lupaConta.textContent = String(i + 1);
  }
}

class HHPdpCompra extends HTMLElement {
  #ctrl = new AbortController();
  #dados = null;
  #fmt = null;

  connectedCallback() {
    try {
      this.#dados = JSON.parse(this.querySelector('[data-hh-dados]')?.textContent || '{}');
    } catch (e) {
      this.#dados = {};
    }
    const moeda = this.#dados.moeda || 'BRL';
    this.#fmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda });
    this.form = this.querySelector('[data-hh-form]');
    this.botao = this.querySelector('[data-hh-adicionar]');
    this.erro = this.querySelector('[data-hh-erro]');
    this.secao = this.closest('.hh-pdp');
    this.barra = this.secao?.querySelector('[data-hh-barra]');
    const { signal } = this.#ctrl;

    this.form?.addEventListener('change', () => this.atualiza(), { signal });
    this.form?.addEventListener('submit', (e) => this.envia(e), { signal });
    this.atualiza();
    this.#ligaBarra();
  }

  disconnectedCallback() {
    this.#ctrl.abort();
    this.obs?.disconnect();
    document.documentElement.classList.remove('hh-com-barra');
  }

  get variante() {
    const lista = this.#dados.variantes || [];
    const marcada = this.form?.querySelector('[data-hh-variante]:checked, [data-hh-id]');
    const id = Number(marcada?.value);
    return lista.find((v) => v.id === id) || lista[0];
  }

  get plano() {
    const r = this.form?.querySelector('[data-hh-plano]:checked');
    if (!r) return { q: 1, clube: false, nome: '', duracao: '' };
    const clube = r.dataset.hhPlano === 'clube';
    return { q: clube ? 1 : Number(r.value), clube, nome: r.dataset.nome || '', duracao: r.dataset.duracao || '' };
  }

  dinheiro(centavos) {
    return this.#fmt.format(Math.round(centavos) / 100);
  }

  /** Texto do editor (dados.textos) ou o padrão, quando vazio ou ausente. */
  texto(chave) {
    const t = (this.#dados.textos || {})[chave];
    return typeof t === 'string' && t.trim() !== '' ? t : TEXTOS_PADRAO[chave];
  }

  /** Mesma regra de snippets/hh-pdp-linha-cartao.liquid. */
  linhaCartao(total, parcelas, comPix) {
    const juros = parcelas > 1 && this.#dados.semJuros && this.texto('semJuros') ? ` ${escapa(this.texto('semJuros'))}` : '';
    let modelo;
    if (comPix) modelo = parcelas > 1 ? this.texto('cartaoParcelado') : this.texto('cartaoAvista');
    else modelo = this.texto('parcela');
    // espaço comum no lugar do espaço fixo do Intl, para bater com o filtro money do Liquid
    const brl = (c) => this.dinheiro(c).replace(/ /g, ' ');
    return montaHtml(modelo, {
      juros,
      total: brl(total),
      valor: brl(Math.round(total / parcelas)),
      parcelas: String(parcelas),
    });
  }

  total(v, q, clube) {
    if (!v) return 0;
    if (clube && v.clube) return v.clube;
    const d = Number((this.#dados.descontos || {})[String(q)] || 0);
    return Math.round(v.preco * q * (100 - d) / 100);
  }

  atualiza() {
    const v = this.variante;
    if (!v) return;
    const { q, clube, nome, duracao } = this.plano;
    const d = this.#dados;

    // preço dos cartões e estoque por quantidade
    this.querySelectorAll('[data-hh-plano]').forEach((r) => {
      const ehClube = r.dataset.hhPlano === 'clube';
      const qq = ehClube ? 1 : Number(r.value);
      const alvo = this.querySelector(`[data-hh-total="${ehClube ? 'clube' : qq}"]`);
      if (alvo) alvo.textContent = this.dinheiro(this.total(v, qq, ehClube));
      const cada = this.querySelector(`[data-hh-cada="${qq}"]`);
      if (cada && !ehClube) cada.textContent = this.dinheiro(this.total(v, qq, false) / qq);
      if (!ehClube) r.disabled = !v.disponivel || v.limite < qq;
    });
    const marcado = this.form?.querySelector('[data-hh-plano]:checked');
    if (marcado?.disabled) {
      const livre = this.form.querySelector('[data-hh-plano]:not(:disabled)');
      if (livre) { livre.checked = true; return this.atualiza(); }
    }

    // assinatura do Clube
    const sp = this.form?.querySelector('[data-hh-plano-venda]');
    if (sp) sp.disabled = !clube;

    const total = this.total(v, q, clube);
    const pix = Number(d.pix || 0);
    const parcelas = Number(d.parcelas || 1);
    const valorPix = Math.round(total * (100 - pix) / 100);

    const elPix = this.querySelector('[data-hh-valor-pix]');
    if (elPix) elPix.textContent = this.dinheiro(valorPix);
    const elValor = this.querySelector('[data-hh-valor]');
    if (elValor) elValor.textContent = this.dinheiro(total);
    const elDe = this.querySelector('[data-hh-de]');
    if (elDe) {
      const de = v.de * q;
      const linhaDe = elDe.closest('[data-hh-de-linha]') || elDe;
      linhaDe.hidden = !(de > total) || clube;
      elDe.lastChild.textContent = this.dinheiro(de);
      // tag "-25%": desconto do total escolhido (com o desconto de 3 ou 6 frascos) sobre o preço anterior
      const off = this.querySelector('[data-hh-off]');
      if (off) off.hidden = linhaDe.hidden;
      if (off && de > total) {
        const pct = Math.round(((de - total) * 100) / de);
        off.querySelector('[data-hh-off-vis]').textContent = `-${pct}%`;
        off.querySelector('[data-hh-off-sr]').textContent = `${pct}% de desconto`;
      }
    }
    const linha = this.querySelector('[data-hh-linha-cartao]');
    // a linha do cartão mostra as parcelas ("ou 6x de R$ 21,65"): o "Por" já está escrito acima
    if (linha && parcelas > 1) linha.innerHTML = this.linhaCartao(total, parcelas, false);

    // botão e barra
    if (this.botao) {
      this.botao.disabled = !v.disponivel;
      const rot = this.botao.querySelector('[data-hh-rotulo]');
      if (rot) rot.textContent = v.disponivel ? d.textoAdicionar : d.textoEsgotado;
    }
    const bPreco = this.barra?.querySelector('[data-hh-barra-preco]');
    if (bPreco) bPreco.textContent = this.dinheiro(pix > 0 ? valorPix : total);
    const bPlano = this.barra?.querySelector('[data-hh-barra-plano]');
    if (bPlano && nome) {
      bPlano.textContent = duracao
        ? this.texto('barraPlano').split('{nome}').join(nome).split('{duracao}').join(duracao)
        : nome;
    }
    const bBotao = this.barra?.querySelector('[data-hh-adicionar-barra]');
    if (bBotao) bBotao.disabled = !v.disponivel;
  }

  async envia(e) {
    e.preventDefault();
    const v = this.variante;
    if (!v || !v.disponivel) return;
    const { q, clube } = this.plano;
    const sp = this.form.querySelector('[data-hh-plano-venda]');
    const item = { id: v.id, quantity: q };
    if (clube && sp) item.selling_plan = Number(sp.value);

    const secoes = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    const botoes = [this.botao, this.barra?.querySelector('[data-hh-adicionar-barra]')].filter(Boolean);
    const rot = this.botao?.querySelector('[data-hh-rotulo]');
    botoes.forEach((b) => b.setAttribute('aria-busy', 'true'));
    if (rot) rot.textContent = this.#dados.textoAdicionando || rot.textContent;
    if (this.erro) this.erro.hidden = true;

    try {
      const rota = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
      const resp = await fetch(rota, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: secoes.join(',') }),
      });
      const json = await resp.json();
      if (!resp.ok || json.status) throw new Error(json.description || json.message || '');

      let carrinho;
      try {
        carrinho = await (await fetch('/cart.js')).json();
      } catch (_) {
        carrinho = undefined;
      }
      document.dispatchEvent(
        new CartAddEvent(carrinho, this.id || 'hh-pdp', {
          source: 'hh-pdp',
          itemCount: q,
          productId: String(this.dataset.section || ''),
          variantId: String(v.id),
          sections: json.sections,
        })
      );
      if (rot) rot.textContent = this.#dados.textoAdicionado || this.#dados.textoAdicionar;
      // confirmação visual (assets/hh-motion.css): fundo verde e sinal por 2,4 s
      botoes.forEach((b) => b.setAttribute('data-hh-estado', 'adicionado'));
      setTimeout(() => {
        botoes.forEach((b) => b.removeAttribute('data-hh-estado'));
        this.atualiza();
      }, 2400);
      if (!document.querySelector('cart-drawer-component')) {
        window.location.href = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
      }
    } catch (err) {
      if (this.erro) {
        this.erro.textContent = err && err.message ? err.message : this.#dados.textoErro;
        this.erro.hidden = false;
      }
      this.atualiza();
    } finally {
      botoes.forEach((b) => b.removeAttribute('aria-busy'));
    }
  }

  #ligaBarra() {
    const alvo = this.querySelector('[data-hh-adicionar]') || this.querySelector('[data-hh-compra-alvo]');
    if (!this.barra || !alvo) return;
    const mq = window.matchMedia('(max-width: 899px)');
    const html = document.documentElement;
    let passou = false;
    const aplica = () => {
      const mostra = passou && mq.matches;
      this.barra.classList.toggle('is-visivel', mostra);
      this.barra.inert = !mostra;
      html.classList.toggle('hh-com-barra', mostra);
    };
    this.obs = new IntersectionObserver((entradas) => {
      const en = entradas[0];
      passou = !en.isIntersecting && en.boundingClientRect.top < 0;
      aplica();
    });
    this.obs.observe(alvo);
    mq.addEventListener('change', aplica, { signal: this.#ctrl.signal });
  }
}

class HHPdpRecomendacoes extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url || this.dataset.carregado) return;
    const busca = () => {
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const novo = doc.querySelector('hh-pdp-recomendacoes');
          if (novo && novo.querySelector('[data-hh-rec-item]')) {
            this.innerHTML = novo.innerHTML;
            this.closest('.hh-pdp-recs-sec')?.removeAttribute('data-vazia');
          }
          this.dataset.carregado = '1';
        })
        .catch(() => {});
    };
    const obs = new IntersectionObserver(
      (en) => {
        if (en[0].isIntersecting) { obs.disconnect(); busca(); }
      },
      { rootMargin: '1500px 0px' }
    );
    obs.observe(this.closest('.hh-pdp-recs-sec') || this);
  }
}

if (!customElements.get('hh-pdp-galeria')) customElements.define('hh-pdp-galeria', HHPdpGaleria);
if (!customElements.get('hh-pdp-compra')) customElements.define('hh-pdp-compra', HHPdpCompra);
if (!customElements.get('hh-pdp-recomendacoes')) customElements.define('hh-pdp-recomendacoes', HHPdpRecomendacoes);
